import { useState, useEffect, useRef, useCallback } from 'react'

import debounce from 'lodash.debounce'

import axios from 'axios'

import { SEARCH_EVENT_SOURCE } from 'Pods/Shared/UserTracker/UserTracker'

import { sendExperimentEvent } from 'Utils/Experiments'
import { buildLocalizedPath } from 'Utils/getLocalizedUrl'

// The React search page registers its product type toggle (all / model / 3d print); every other
// page has none and keeps reporting 'model'.
const getContentType = () => (
  typeof window.getSearchPageContentType === 'function' ? window.getSearchPageContentType() : 'model'
)

// suggestionsDisabled: the search-dropdown panel (HomeSearchDropdown) replaces the flat
// suggestion list entirely - skip the legacy live_search fetching and its keyboard
// handling so no invisible suggestion can be selected or submitted.
function useSearch(isInListingPage = false, searchInputValue = '', suggestionsDisabled = false) {
  const [query, setQuery] = useState(searchInputValue || '')
  const [data, setData] = useState([])
  const [cursorPosition, setCursorPosition] = useState(-1)
  const [areArrowsUsed, setAreArrowsUsed] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  const wrapperRef = useRef(null)
  const eventSource = SEARCH_EVENT_SOURCE.TOP_BAR
  const currentRequest = useRef(null)
  const isRequestPending = useRef(false)
  const requestCounter = useRef(0)

  // Cleanup function to cancel any pending requests
  useEffect(() => {
    return () => {
      if (currentRequest.current) {
        currentRequest.current.cancel()
      }
      if (debouncedChange && debouncedChange.cancel) {
        debouncedChange.cancel()
      }
      isRequestPending.current = false
    }
  }, [])

  // handleSubmit sets `submitting` before a full-page navigation. When the user returns via the
  // browser back/forward cache the page is restored with the JS heap frozen, leaving the loader stuck.
  // `pageshow` fires on bfcache restore (event.persisted === true); reset the spinner there.
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        setSubmitting(false)
      }
    }

    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        closeSuggestions()
        setIsFocused(false)
        // Blur so the next click on the input refires `focus` and reopens the dropdown -
        // clicking non-focusable page content leaves the input focused otherwise.
        if (wrapperRef.current.contains(document.activeElement)) {
          document.activeElement.blur()
        }
      }
    }

    if (showSuggestions || isFocused) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showSuggestions, isFocused, closeSuggestions])

  async function handleSuggestions(inputValue) {
    if (isRequestPending.current) {
      return
    }

    isRequestPending.current = true
    requestCounter.current += 1
    const currentRequestId = requestCounter.current

    try {
      // Cancel previous request if it exists
      if (currentRequest.current) {
        currentRequest.current.cancel()
      }

      // Create a new cancel token
      const cancelToken = axios.CancelToken.source()
      currentRequest.current = cancelToken

      const response = await axios.get('/api/internal/items/live_search', {
        params: { query: inputValue },
        cancelToken: cancelToken.token,
        timeout: 3000,
        headers: {
          'Cache-Control': 'no-cache',
        },
      })

      if (currentRequestId === requestCounter.current) {
        onFetchDataSuccess(response)
      }
    } catch (error) {
      if (!axios.isCancel(error) && currentRequestId === requestCounter.current) {
        // eslint-disable-next-line no-console
        console.error('Error fetching search suggestions:', error)
        setData([])
        setShowSuggestions(false)
      }
    } finally {
      isRequestPending.current = false
    }
  }

  function onFetchDataSuccess(response) {
    // temporary hotfix for the new search suggestion list
    const newData = Array.isArray(response.data?.data)
      ? response.data.data
      : response.data || []

    setData(newData)
    sendExperimentEvent('autocomplete_search_suggestion', newData?.autocomplete_search_suggestion)
    setShowSuggestions(true)
  }

  // Create debounced function using useCallback for stability
  const debouncedChange = useCallback(
    debounce(async (value) => {
      if (!value || !value.trim()) {
        setData([])
        setShowSuggestions(false)
        return
      }

      await handleSuggestions(value)
    }, 300),
    []
  )

  function updateQuery(value) {
    setQuery(value)
    setCursorPosition(-1)
    setAreArrowsUsed(false)

    if (suggestionsDisabled) {
      return
    }

    if (value.length > 0) {
      debouncedChange(value)
    } else {
      debouncedChange.cancel()
      setData([])
      setShowSuggestions(false)
    }
  }

  function onInputChange(event) {
    updateQuery(event.target.value)
  }

  function handleClear() {
    updateQuery('')
  }

  function handleKeyDown(e) {
    // In dropdown mode there is no suggestion list to walk - leave the arrow keys to the
    // browser and only handle Escape.
    if (suggestionsDisabled && e.keyCode !== 27) {
      return
    }

    // arrow up/down button should select next/previous list element
    if (e.keyCode === 38) {
      e.preventDefault()
      setAreArrowsUsed(true)
      if (cursorPosition > 0) {
        setCursorPosition(cursorPosition - 1)
      }
    } else if (e.keyCode === 40) {
      e.preventDefault()
      setAreArrowsUsed(true)
      if (cursorPosition < data.length - 1) {
        setCursorPosition(cursorPosition + 1)
      }
    } else if (e.keyCode === 27) {
      setShowSuggestions(false)
      setCursorPosition(-1)
      setAreArrowsUsed(false)
      setIsFocused(false)
      // Blur so a subsequent click refires `focus` and reopens the dropdown.
      e.target?.blur?.()
    }
  }

  function handleFocus() {
    setIsFocused(true)
  }

  function handleFocusLeave(event) {
    // Close when keyboard focus moves outside the search wrapper (Tab / Shift+Tab).
    // relatedTarget is null on pointer interactions with non-focusable targets (and on
    // Safari link clicks); those belong to the document mousedown handler - closing here
    // would unmount a clicked dropdown link mid-click and swallow its navigation.
    const nextTarget = event.relatedTarget
    if (!nextTarget) {
      return
    }

    if (wrapperRef.current && !wrapperRef.current.contains(nextTarget)) {
      closeSuggestions()
      setIsFocused(false)
    }
  }

  function getSearchParams() {
    if (isInListingPage) {
      // this logic makes sense for listing page only
      const currentSearch = window.location.search
      const currentParams = new URLSearchParams(currentSearch)

      // if we have keywords, remove them
      if (currentParams.has('keywords')) {
        currentParams.delete('keywords')
      }

      // always jump to first page with new search
      if (currentParams.has('page')) {
        currentParams.delete('page')
      }

      // if we have suggested_keywords, remove them
      if (currentParams.has('suggested_keywords')) {
        currentParams.delete('suggested_keywords')
      }

      // The suggestion marker belongs to the search that was picked, not to the next one: a typed
      // search would otherwise be named `suggested_` in analytics (ExtractSearchQuery).
      if (currentParams.has('suggested')) {
        currentParams.delete('suggested')
      }

      // if we have image_id param, from similar image search flow, remove that.
      if (currentParams.has('image_id')) {
        currentParams.delete('image_id')
      }

      if (currentParams.has('disable_fuzzy_matcher')) {
        currentParams.delete('disable_fuzzy_matcher')
      }

      // "Search within results" narrows the previous query only; a new query starts without it.
      // The bracketed key covers hand-crafted array URLs, which the backend also accepts.
      currentParams.delete('within_keywords')
      currentParams.delete('within_keywords[]')

      // if we have some params, add them with &
      if (currentParams.size) {
        return `&${currentParams.toString()}`
      }

      return ''
    }

    return ''
  }

  function handleSubmit(e) {
    e.preventDefault()
    setSubmitting(true)

    // Cancel any pending requests
    if (currentRequest.current) {
      currentRequest.current.cancel()
    }

    // Cancel any pending debounced calls
    if (debouncedChange && debouncedChange.cancel) {
      debouncedChange.cancel()
    }

    const hasData = data.length > 0
    const hasSelectedOption = areArrowsUsed && cursorPosition > -1
    const selectedKeyword = hasData && hasSelectedOption ? data[cursorPosition][0] : null

    // Enter on a highlighted suggestion is a suggestion pick, not a typed search.
    if (selectedKeyword && window.trackSuggestionClick) {
      window.trackSuggestionClick({ value: selectedKeyword, searchTerm: query, eventSource })
    } else if (!selectedKeyword && window.searchTracker) {
      window.searchTracker({ searchTerm: query, type: getContentType(), eventSource })
    }

    if (hasData && hasSelectedOption) {
      const currentKeyword = selectedKeyword

      // `suggested=1` matches the suggestion row's own link, so a keyboard pick gets the same
      // `suggested_` list name as a click.
      if (currentKeyword) {
        return (window.location.href = buildLocalizedPath(
          `/search?keywords=${encodeURIComponent(currentKeyword)}&suggested=1${getSearchParams()}`
        ))
      }
    }

    return (window.location.href = buildLocalizedPath(
      `/search?keywords=${encodeURIComponent(query)}${getSearchParams()}`
    ))
  }

  function closeSuggestions() {
    setShowSuggestions(false)
    setCursorPosition(-1)
    setAreArrowsUsed(false)
  }

  return {
    query,
    wrapperRef,
    showSuggestions,
    suggestions: data,
    onInputChange,
    handleClear,
    handleKeyDown,
    handleFocus,
    handleFocusLeave,
    isFocused,
    submitting,
    handleSubmit,
    cursorPosition,
    eventSource,
  }
}

export { useSearch }
